(function (scope) {
  'use strict';
  const unique = values => [...new Set(values.filter(Boolean))];
  function parseYmm(value) {
    const parts = String(value).split('|').map(s => s.trim());
    if (parts.length !== 3 || !parts[1] || !parts[2]) return null;
    const match = parts[0].match(/^(\d{4})(?:-(\d{4})|(\+))?$/);
    if (!match && !['Unknown','All'].includes(parts[0])) return null;
    const from = match ? Number(match[1]) : null;
    const to = match ? (match[3] ? null : Number(match[2] || match[1])) : null;
    if (to && to < from) return null;
    return {value, make:parts[1], model:parts[2], from, to, unknown:parts[0] === 'Unknown', all:parts[0] === 'All'};
  }
  const rows = product => (product.ymm || []).map(parseYmm).filter(Boolean);
  const isUniversal = product => (product.ymm || []).includes('Universal');
  const sameVan = (row, van) => row.make === van.make && row.model === van.model;
  const inYear = (row, year) => row.all || row.unknown || (year >= row.from && (!row.to || year <= row.to));
  function filterValues(products, van) {
    const specific = products.flatMap(rows).filter(row => sameVan(row, van) && inYear(row, Number(van.year))).map(row => row.value);
    return unique(specific.concat(products.some(isUniversal) ? ['Universal'] : []));
  }
  function collectionUrl(target, products, van, currentUrl) {
    const values = filterValues(products, van);
    if (!van.make || !van.model || !/^\d{4}$/.test(String(van.year)) || !values.length) return null;
    const current = new URL(currentUrl), result = new URL(target, current.origin);
    if (result.origin !== current.origin) return null;
    if (current.pathname === result.pathname) current.searchParams.forEach((value, key) => {
      if ((key.startsWith('filter.') && key !== 'filter.p.m.fitment.ymm') || key === 'sort_by') result.searchParams.append(key, value);
    });
    // Match Shopify's native multi-checkbox form. Encoding a joined comma string
    // can make the comma part of one literal metafield value.
    for (const value of values) result.searchParams.append('filter.p.m.fitment.ymm', value);
    for (const key of ['make','model','year']) result.searchParams.set('mp_' + key, van[key]);
    // The preview cookie already selects the draft. Shopify's preview redirect
    // collapses repeated query keys, so never re-add preview_theme_id here.
    return result.href;
  }
  // Detail rows: years|make|model|body lengths|roof heights|series|note.
  // Commas join allowed values within one dimension; blank means unrestricted.
  function detailRows(product) {
    return (product.details || []).map(value => {
      const parts=String(value).split('|').map(value=>value.trim());
      const row=parseYmm(parts.slice(0,3).join('|'));
      if(!row || parts.length!==7)return null;
      const list=value=>value?value.split(',').map(v=>v.trim()).filter(Boolean):[];
      return {...row,length:list(parts[3]),roof:list(parts[4]),series:list(parts[5]),note:parts[6]};
    }).filter(Boolean);
  }
  function applications(product, van, variantId, matchYear=true) {
    const detailed=detailRows(product);
    const source=detailed.length?detailed:rows(product).map(row=>({...row,roof:product.roofs||[],length:product.lengths||[],series:product.series||[]}));
    const variant=(product.variants||[]).find(v=>String(v.id)===String(variantId))||{};
    return source.filter(row=>sameVan(row,van)&&(!matchYear||!van.year||inYear(row,Number(van.year)))).map(row=>({...row,
      roof:variant.roofs?.length?variant.roofs:row.roof,
      length:variant.lengths?.length?variant.lengths:row.length
    }));
  }
  function requirements(product,van,variantId) {
    if(isUniversal(product))return {roof:[],length:[],series:[]};
    let options=applications(product,van,variantId);
    if(!options.length)options=applications(product,van,variantId,false);
    return Object.fromEntries(['roof','length','series'].map(key=>[key,options.some(row=>!row[key]?.length)?[]:unique(options.flatMap(row=>row[key]||[]))]));
  }
  function optionValues(products,van,field) {
    const values=products.flatMap(product=>applications(product,van,null,false).flatMap(row=>row[field]||[]));
    if(field==='series'&&van.make==='Ford'&&van.model==='Transit')values.push('E-Transit');
    return unique(values);
  }
  function assess(product, van, variantId, confirmed) {
    if (!van || !van.make || !van.model || !van.year) return {state:'pending', reasons:[]};
    const matches = rows(product).filter(row => sameVan(row, van));
    if (!matches.length && !isUniversal(product)) return {state:'no', reasons:['reason_model']};
    const yearMatches = matches.filter(row => inYear(row, Number(van.year)));
    if (!yearMatches.length && !isUniversal(product)) return {state:'no', reasons:['reason_year']};
    const variant = (product.variants || []).find(v => String(v.id) === String(variantId)) || {};
    const reasons=[],notes=[],missing=[];
    if(!isUniversal(product)) {
      if(!yearMatches.some(row=>!row.unknown))reasons.push('reason_unknown');
      const options=applications(product,van,variantId);
      if(!options.length)return {state:'no',reasons:['reason_year']};
      const checks=options.map(row=>{
        const absent=[],conflicts=[];
        for(const key of ['roof','length','series']){
          if(!row[key]?.length)continue;
          if(!van[key])absent.push(key);
          else if(!row[key].includes(van[key]))conflicts.push(key);
        }
        return {row,absent,conflicts};
      });
      const viable=checks.filter(check=>!check.conflicts.length).sort((a,b)=>a.absent.length-b.absent.length);
      if(!viable.length)return {state:'no',reasons:['reason_'+checks.sort((a,b)=>a.conflicts.length-b.conflicts.length)[0].conflicts[0]]};
      missing.push(...viable[0].absent);
      if(viable[0].row.note)notes.push(viable[0].row.note);
    }
    if(missing.length)reasons.push('reason_details');
    // Product notes explain installation; an unresolved variant note requires review.
    if(product.note)notes.push(product.note);
    if(variant.note){notes.push(variant.note);reasons.push('reason_variant');}
    if(product.conditions&&!confirmed)reasons.push('reason_conditions');
    return {state:reasons.length?'review':'fit',reasons,notes,missing};
  }
  const engine = {unique, parseYmm, rows, isUniversal, filterValues, collectionUrl, detailRows, applications, requirements, optionValues, assess};
  if (typeof module !== 'undefined' && module.exports) module.exports = engine;
  scope.MelipronFitment = engine;
})(typeof window !== 'undefined' ? window : globalThis);
